/**
 * OverrideForceControl — #6 flujo A: el "Forzar" humano de una invariante
 * overridable (hoy solo "período cerrado") en una tarjeta de aprobación.
 *
 * El backend, cuando una acción choca con una invariante overridable, NO bloquea
 * el preview: lo deja pasar y adjunta `preview._override_required` (+ un token
 * `_override_scope`). Este control muestra el aviso y, para un rol suficiente,
 * ofrece "Forzar de todas formas" → pide un motivo → llama `onForce(reason)`.
 * El padre reenvía el turno de confirmación con `override:{reason, scope}`.
 *
 * La autorización real la impone el backend (rol owner/accountant + auditoría
 * AI_WRITE_OVERRIDE); el gate de rol aquí es solo UX.
 */

import { useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function OverrideForceControl({ violations, canForce, onForce, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const list = Array.isArray(violations) ? violations.filter((v) => v && v.message) : [];
  if (list.length === 0) return null;

  const submit = () => {
    const r = reason.trim();
    if (!r) return;
    onForce(r);
  };

  return (
    <div className="mt-1 max-w-sm space-y-1.5 rounded-lg border border-orange-300 bg-orange-50/70 px-3 py-2 text-xs dark:border-orange-700 dark:bg-orange-900/30">
      {list.map((v, i) => (
        <div key={`${v.code}-${i}`} className="flex items-start gap-1.5 text-orange-800 dark:text-orange-200">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{v.message}</span>
        </div>
      ))}

      {!canForce ? (
        <p className="text-[11px] text-orange-700/80 dark:text-orange-300/80">
          Requiere un responsable (propietario o contable) para forzarlo.
        </p>
      ) : !expanded ? (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setExpanded(true)}
          className="h-7 w-full border-orange-400 text-xs text-orange-800 hover:bg-orange-100 dark:border-orange-600 dark:text-orange-200"
        >
          <ShieldAlert className="mr-1 h-3.5 w-3.5" /> Forzar de todas formas
        </Button>
      ) : (
        <div className="space-y-1.5">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Motivo del forzado (queda auditado)…"
            className="w-full rounded-md border border-orange-300 bg-white px-2 py-1 text-[11px] text-foreground outline-none focus:border-orange-500 dark:border-orange-700 dark:bg-slate-900"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => { setExpanded(false); setReason(''); }}
              className="h-7 flex-1 text-xs"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={disabled || !reason.trim()}
              onClick={submit}
              className="h-7 flex-1 bg-orange-600 text-xs hover:bg-orange-700"
            >
              Confirmar forzado
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
